import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { ErrorState } from '@/shared/errors/ErrorState';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import {
  BarChart3,
  ChevronDown,
  Download,
  FileText,
  Mail,
  MoreHorizontal,
  Plus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { PaginationControls, usePagination, paginateData } from '@/shared/components/pagination';
import { useProjectAreaTagLocationOptions } from '@/features/area-tag-sets';
import { PunchListsNavigation } from './PunchListsNavigation';
import { PunchListsTable } from './PunchListsTable';
import { PunchListsFilters } from './PunchListsFilters';
import { PunchListsDashboard } from './PunchListsDashboard';
import { PunchListCreateDialog } from './PunchListCreateDialog';
import { UnitTourReportDialog, type UnitTourReportUnit } from './UnitTourReportDialog';
import { UnitReportShareDialog } from './UnitReportShareDialog';
import { PunchListExportDialog } from './PunchListExportDialog';
import {
  UnitItemsActionDialog,
  type UnitItemsAction,
  type UnitItemsActionSelection,
} from './UnitItemsActionDialog';
import {
  useProjectLayouts,
  useProjectUsers,
  usePunchListFilterViews,
  usePunchListItemTypes,
  usePunchListItem,
  usePunchListItems,
  useTrades,
  usePunchListMutations,
  usePunchListExportPreferences,
  useUpdatePunchListExportPreferences,
} from '../hooks';
import { getPunchListItems } from '../api/api';
import { handleApiError } from '@/shared/api/requests';
import { useToast } from '@/shared/hooks/use-toast';
import {
  type PunchListFilterView,
  type PunchListItemFilters,
  type PunchListItemSummary,
  type PunchListExportPreferences,
  type PunchListCsvColumnKey,
  type PunchListsSortKey,
  type PunchListsSortState,
  formatUnitLabel,
} from '../types';
// Importing for the side effects: ``registerTour`` runs at module load
// for each of the punch-list tour variants.
import { PUNCH_LISTS_LIST_TOUR_ID } from '@/features/tours/punch-lists/list';
import { PUNCH_LISTS_COMBINED_TOUR_ID } from '@/features/tours/punch-lists/combined';
// detail tour registration also happens via combined's neighbor; we
// don't need its id here but we want it registered for the detail page
// import to find it when the user navigates over.
import '@/features/tours/punch-lists/detail';
import { useTour } from '@/features/tours';
import { useAllUserProjects } from '@/shared/hooks/useUserProjects';
import { usePunchListReport } from '../hooks/usePunchListReport';
import { useUnitTourReport } from '../hooks/useUnitTourReport';
import { sortListItemsByRules } from '@/shared/utils/listSorting';
import { normalizePunchListsSortState } from '../utils/sortState';

interface CsvExportRequest {
  items: PunchListItemSummary[];
  fileNamePrefix?: string;
  preferences: PunchListExportPreferences;
}

export const PunchListsPage: React.FC = () => {
  const { clientName } = useParams<{ clientName?: string }>();
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | undefined>();
  const [filters, setFilters] = useState<PunchListItemFilters>({});
  const [sort, setSort] = useState<PunchListsSortState[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [summaryExpandedOverride, setSummaryExpandedOverride] = useState<boolean | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  const [unitTourDialogOpen, setUnitTourDialogOpen] = useState(false);
  const [unitReportShareDialogOpen, setUnitReportShareDialogOpen] = useState(false);
  const [unitItemsAction, setUnitItemsAction] = useState<UnitItemsAction | null>(null);
  const [unitItemsActionPending, setUnitItemsActionPending] = useState(false);
  const [generatingUnitTourReports, setGeneratingUnitTourReports] = useState(false);
  const [csvExportRequest, setCsvExportRequest] = useState<CsvExportRequest | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  const serverFilters = useMemo<PunchListItemFilters>(
    () => getServerFilters(filters),
    [
      filters.status,
      filters.priority,
      filters.assignee,
      filters.search,
      filters.hotspot_id,
      filters.photo_id,
      filters.area_tag_id,
      filters.area_tag_set_id,
    ]
  );

  const itemQuery = usePunchListItems(numericProjectId, serverFilters, { progressive: true });
  const { data: items, isLoading, error, isComplete } = itemQuery;
  const { data: editingItem } = usePunchListItem(editingItemId, numericProjectId);
  const exportQuery = usePunchListItems(numericProjectId, {});
  const { data: allProjectItems } = exportQuery;
  const { data: filterViews } = usePunchListFilterViews(numericProjectId);
  const { data: itemTypes } = usePunchListItemTypes();
  const { data: trades } = useTrades();
  const { data: projectUsers } = useProjectUsers(numericProjectId);
  const { data: projectLocationOptions } = useProjectAreaTagLocationOptions(numericProjectId);
  const { data: projectLayouts } = useProjectLayouts(numericProjectId);
  const { allProjects } = useAllUserProjects(clientName);
  const { exportItemsToCsv, generatePunchListPdfReport } = usePunchListReport();
  const { generateUnitTourReports } = useUnitTourReport();
  const { deleteItem, sendReminders, shareUnitReports } = usePunchListMutations(numericProjectId);
  const exportPreferences = usePunchListExportPreferences(numericProjectId);
  const updateExportPreferences = useUpdatePunchListExportPreferences(numericProjectId);

  const allItems = useMemo(() => items ?? [], [items]);
  const exportSourceItems = useMemo(() => allProjectItems ?? [], [allProjectItems]);
  const assigneeOptions = useMemo(() => {
    return [...(projectUsers ?? [])].sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
    );
  }, [projectUsers]);
  const typeOptions = useMemo(
    () => [...(itemTypes ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [itemTypes]
  );
  const tradeOptions = useMemo(
    () => [...(trades ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [trades]
  );
  const locationOptions = useMemo(() => {
    return [...(projectLocationOptions?.areaTags ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [projectLocationOptions]);
  const areaTagSetOptions = useMemo(() => {
    return [...(projectLocationOptions?.areaTagSets ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true })
    );
  }, [projectLocationOptions]);
  // A unit is identified by (layout, name) — the same unit name can exist in
  // multiple buildings (layouts), so dedupe by layout+name and label with the
  // building prefix (e.g. "Building A - 107").
  const unitReportOptions = useMemo(() => {
    const byKey = new Map<string, UnitTourReportUnit>();
    for (const unit of locationOptions) {
      const key = `${unit.layout_id ?? ''}::${unit.name}`;
      if (!byKey.has(key)) byKey.set(key, unit);
    }
    return Array.from(byKey.values());
  }, [locationOptions]);
  const layoutReportOptions = useMemo(
    () =>
      [...(projectLayouts ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      ),
    [projectLayouts]
  );
  const hasUnitAreaReportOptions = layoutReportOptions.length > 0 || unitReportOptions.length > 0;
  const filteredItems = useMemo(() => filterPunchListItems(allItems, filters), [allItems, filters]);
  const sortedItems = useMemo(() => sortPunchListItems(filteredItems, sort), [filteredItems, sort]);
  const selectableItemIds = useMemo(() => sortedItems.map((item) => item.id), [sortedItems]);
  const selectedItems = useMemo(
    () => exportSourceItems.filter((item) => selectedItemIds.has(item.id)),
    [exportSourceItems, selectedItemIds]
  );
  const selectedCount = selectedItemIds.size;
  const allSelectableSelected =
    selectableItemIds.length > 0 && selectableItemIds.every((id) => selectedItemIds.has(id));
  const partiallySelected =
    selectableItemIds.some((id) => selectedItemIds.has(id)) && !allSelectableSelected;
  const filteredCount = sortedItems.length;
  const projectName = allProjects.find((project) => project.id === numericProjectId)?.name;
  const punchItemBaseUrl = useMemo(() => {
    if (!numericProjectId) return '';
    const projectPath = clientName
      ? `${encodeURIComponent(clientName)}/${numericProjectId}`
      : String(numericProjectId);
    return `${window.location.origin}/app/property/${projectPath}/punch-lists`;
  }, [clientName, numericProjectId]);
  const exportableTypes = useMemo(() => itemTypes ?? [], [itemTypes]);
  const visibleCountByType = useMemo(() => {
    const counts = new Map<string, number>();
    exportSourceItems.forEach((item) => {
      const typeId = item.item_type?.id;
      if (!typeId) return;
      counts.set(typeId, (counts.get(typeId) ?? 0) + 1);
    });
    return counts;
  }, [exportSourceItems]);

  const [paginationState, paginationActions, paginationInfo] = usePagination(filteredCount, 10);
  const pagedItems = useMemo(
    () => paginateData(sortedItems, paginationState.currentPage, paginationState.itemsPerPage),
    [sortedItems, paginationState.currentPage, paginationState.itemsPerPage]
  );

  useEffect(() => {
    if (!allProjectItems) return;
    const availableIds = new Set(exportSourceItems.map((item) => item.id));
    setSelectedItemIds((current) => {
      const next = new Set([...current].filter((id) => availableIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [allProjectItems, exportSourceItems]);

  const handleFiltersChange = (next: PunchListItemFilters) => {
    setFilters(next);
    paginationActions.setCurrentPage(1);
  };

  const handleFilterViewApply = (view: PunchListFilterView) => {
    setFilters(view.filters ?? {});
    setSort(normalizePunchListsSortState(view.sort_state));
    paginationActions.setCurrentPage(1);
  };

  const handleSortChange = (next: PunchListsSortState[]) => {
    setSort(next);
    paginationActions.setCurrentPage(1);
  };

  const handleColumnSortChange = (key: PunchListsSortKey) => {
    setSort((current) => {
      const primary = current[0];
      if (primary?.key === key) {
        return [
          { ...primary, direction: primary.direction === 'asc' ? 'desc' : 'asc' },
          ...current.slice(1),
        ];
      }
      return [{ key, direction: 'asc' }];
    });
    paginationActions.setCurrentPage(1);
  };

  const getItemsForFilterView = async (view: PunchListFilterView) => {
    if (!numericProjectId) return [];

    const baseItems = await getPunchListItems(
      numericProjectId,
      getServerFilters(view.filters ?? {})
    );
    const filtered = filterPunchListItems(baseItems, view.filters ?? {});
    return sortPunchListItems(filtered, normalizePunchListsSortState(view.sort_state));
  };

  const openCsvExportDialog = async (
    exportItems: PunchListItemSummary[],
    fileNamePrefix?: string,
  ) => {
    let preferences = exportPreferences.data;
    let preferenceError = exportPreferences.error;
    if (!preferences) {
      const result = await exportPreferences.refetch();
      preferences = result.data;
      preferenceError = result.error;
    }
    if (!preferences) {
      toast({
        title: 'Unable to load export columns',
        description: preferenceError
          ? handleApiError(preferenceError)
          : 'Please try exporting again.',
        variant: 'destructive',
      });
      return;
    }
    setCsvExportRequest({ items: exportItems, fileNamePrefix, preferences });
  };

  const exportFilterViewToCsv = async (view: PunchListFilterView) => {
    try {
      await openCsvExportDialog(await getItemsForFilterView(view));
    } catch (error) {
      toast({
        title: 'Unable to export punch items',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  const handleCsvExport = async (selectedColumns: PunchListCsvColumnKey[]) => {
    if (!csvExportRequest) return;
    try {
      await updateExportPreferences.mutateAsync(selectedColumns);
      exportItemsToCsv(
        csvExportRequest.items,
        csvExportRequest.fileNamePrefix,
        selectedColumns,
        punchItemBaseUrl,
      );
      setCsvExportRequest(null);
    } catch (error) {
      toast({
        title: 'Unable to save export columns',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  const generateFilterViewReport = async (view: PunchListFilterView) => {
    generatePunchListPdfReport(
      await getItemsForFilterView(view),
      view.name,
      projectName,
      numericProjectId
    );
  };

  const handleUnitItemsAction = async (
    selection: UnitItemsActionSelection,
    action: UnitItemsAction
  ) => {
    if (!numericProjectId) return;

    const scopeLabel = selection.unit
      ? formatUnitLabel(selection.unit)
      : (selection.layoutName ?? `Layout ${selection.layoutId}`);
    const scopeFilters: PunchListItemFilters = selection.unit
      ? { area_tag_id: selection.unit.id }
      : { layout_id: selection.layoutId };
    const scopeItemsPromise = getPunchListItems(numericProjectId, scopeFilters);

    if (action === 'report') {
      setUnitItemsAction(null);
      void generatePunchListPdfReport(
        scopeItemsPromise,
        `${scopeLabel} Punch List`,
        projectName,
        numericProjectId
      );
      return;
    }

    setUnitItemsActionPending(true);
    try {
      const scopeItems = await scopeItemsPromise;
      await openCsvExportDialog(scopeItems, `punch-lists-${scopeLabel}`);
      setUnitItemsAction(null);
    } catch (error) {
      toast({
        title: 'Unable to export punch items',
        description: handleApiError(error),
        variant: 'destructive',
      });
    } finally {
      setUnitItemsActionPending(false);
    }
  };

  const generateUnitReports = async (units: UnitTourReportUnit[]) => {
    if (!numericProjectId || units.length === 0) return;

    setGeneratingUnitTourReports(true);
    let generatedCount = 0;
    let failedUnits: string[] = [];

    try {
      const result = await generateUnitTourReports(numericProjectId, units, projectName);
      generatedCount = result.generatedCount;
      failedUnits = result.failedUnits;
    } catch (error) {
      toast({
        title: 'Unable to generate QR Punch Walk report',
        description: handleApiError(error),
        variant: 'destructive',
      });
      return;
    } finally {
      setGeneratingUnitTourReports(false);
    }

    if (generatedCount === 0) {
      toast({
        title: 'Unable to generate QR Punch Walk reports',
        description: 'No selected units could be generated.',
        variant: 'destructive',
      });
      return;
    }

    if (failedUnits.length === 0) {
      setUnitTourDialogOpen(false);
      if (units.length > 1) {
        toast({
          title: 'QR Punch Walk report generated',
          description: `${generatedCount} unit${generatedCount === 1 ? '' : 's'} added to one printable report.`,
        });
      }
      return;
    }

    setUnitTourDialogOpen(false);

    toast({
      title: 'QR Punch Walk report partially generated',
      description: `${generatedCount} generated. Failed: ${failedUnits.join(', ')}.`,
      variant: 'destructive',
    });
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    setSelectedItemIds((current) => {
      const next = new Set(current);
      if (checked) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (!isComplete) return;
    setSelectedItemIds((current) => {
      const next = new Set(current);
      selectableItemIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const sendReminderForItems = async (itemsForReminder: PunchListItemSummary[]) => {
    if (!numericProjectId || itemsForReminder.length === 0) return;

    try {
      const result = await sendReminders.mutateAsync({
        project_id: numericProjectId,
        item_ids: itemsForReminder.map((item) => item.id),
      });
      toast({
        title: 'Reminder emails queued',
        description: `${result.item_count} item${result.item_count === 1 ? '' : 's'} will be sent to ${result.recipient_count} recipient${result.recipient_count === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      toast({
        title: 'Unable to send reminders',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  const sendFilterViewReminder = async (view: PunchListFilterView) => {
    await sendReminderForItems(await getItemsForFilterView(view));
  };

  const shareUnitReportsByEmail = async (
    units: UnitTourReportUnit[],
    recipientEmails: string[],
    message: string
  ) => {
    if (!numericProjectId || units.length === 0 || recipientEmails.length === 0) return;

    try {
      const result = await shareUnitReports.mutateAsync({
        project_id: numericProjectId,
        area_tag_ids: units.map((unit) => unit.id),
        recipient_emails: recipientEmails,
        message,
      });
      setUnitReportShareDialogOpen(false);
      toast({
        title: 'Unit report emails queued',
        description: `${result.unit_count} unit report${result.unit_count === 1 ? '' : 's'} will be sent to ${result.recipient_count} recipient${result.recipient_count === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      toast({
        title: 'Unable to share unit reports',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  // Auto-start the appropriate Punch Lists tour:
  //   - items exist → start the *combined* tour (list → click row → detail)
  //     so the user sees the full feature in one continuous run.
  //   - no items yet → start the list-only tour so the user learns about
  //     creating items, statuses, filters, etc.
  //
  // We never start a tour that would partly repeat content the user has
  // already seen: if the list-only tour ran earlier (when the project was
  // empty) and items exist now, we skip ``combined`` — the detail tour on
  // the next detail-page visit will pick up the slack.
  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  const hasItems = allItems.length >= 1;
  const isSummaryVisible = summaryExpandedOverride ?? !isMobileViewport;
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return; // don't interrupt a running tour
    if (hasItems) {
      if (hasSeen(PUNCH_LISTS_COMBINED_TOUR_ID)) return;
      if (hasSeen(PUNCH_LISTS_LIST_TOUR_ID)) return;
      void startTour(PUNCH_LISTS_COMBINED_TOUR_ID);
    } else {
      if (hasSeen(PUNCH_LISTS_LIST_TOUR_ID)) return;
      void startTour(PUNCH_LISTS_LIST_TOUR_ID);
    }
  }, [tourHydrated, active, startTour, hasSeen, hasItems]);

  const handleRowClick = (item: PunchListItemSummary) => {
    navigate(item.id);
  };

  const handleDelete = async (item: PunchListItemSummary) => {
    if (!window.confirm(`Delete “${item.title}”? This cannot be undone.`)) return;
    try {
      await deleteItem.mutateAsync(item.id);
      setSelectedItemIds((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
      toast({ title: 'Punch item deleted' });
    } catch (deleteError) {
      toast({
        title: 'Unable to delete punch item',
        description: handleApiError(deleteError),
        variant: 'destructive',
      });
    }
  };

  return (
    <BaseLayout>
      <div className="min-w-0 space-y-6 p-4 md:p-6">
        {/* Page header */}
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-x-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">Punch Lists</h1>
            <p className="mt-1 text-sm text-muted-foreground">Track and close project items.</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-3 mt-1 h-8 gap-2 px-3 text-muted-foreground hover:text-foreground"
              aria-expanded={isSummaryVisible}
              aria-controls="punch-lists-summary"
              data-tour-id="punch-lists-summary-toggle"
              onClick={() =>
                setSummaryExpandedOverride((current) => !(current ?? !isMobileViewport))
              }
            >
              <BarChart3 className="h-4 w-4" />
              {isSummaryVisible ? 'Hide summary' : 'Show summary'}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isSummaryVisible ? 'rotate-180' : ''}`}
              />
            </Button>
          </div>
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={
                    !exportQuery.isComplete || (
                      exportSourceItems.length === 0 &&
                      !hasUnitAreaReportOptions &&
                      unitReportOptions.length === 0
                    )
                  }
                >
                  <MoreHorizontal className="mr-2 h-4 w-4" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 max-w-[calc(100vw-2rem)]">
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    disabled={exportSourceItems.length === 0 && !hasUnitAreaReportOptions}
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-[70vh] w-40 max-w-[calc(100vw-2rem)] overflow-y-auto">
                    <DropdownMenuItem
                      onClick={() => void openCsvExportDialog(selectedItems)}
                      disabled={selectedCount === 0}
                    >
                      Selected ({selectedCount})
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Types</DropdownMenuLabel>
                    {exportableTypes.map((type) => (
                      <DropdownMenuItem
                        key={type.id}
                        onClick={() =>
                          void openCsvExportDialog(
                            exportSourceItems.filter((item) => item.item_type?.id === type.id)
                          )
                        }
                        disabled={(visibleCountByType.get(type.id) ?? 0) === 0}
                      >
                        {type.name}
                      </DropdownMenuItem>
                    ))}
                    {hasUnitAreaReportOptions && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setUnitItemsAction('export')}>
                          By unit or area…
                        </DropdownMenuItem>
                      </>
                    )}
                    {(filterViews ?? []).length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Saved views</DropdownMenuLabel>
                        {(filterViews ?? []).map((view) => (
                          <DropdownMenuItem
                            key={view.id}
                            onClick={() => void exportFilterViewToCsv(view)}
                          >
                            {view.name}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    disabled={exportSourceItems.length === 0 && !hasUnitAreaReportOptions}
                  >
                    <FileText className="h-4 w-4" />
                    Generate report
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-[70vh] w-40 max-w-[calc(100vw-2rem)] overflow-y-auto">
                    <DropdownMenuItem
                      onClick={() =>
                        generatePunchListPdfReport(
                          selectedItems,
                          `Selected Items (${selectedCount})`,
                          projectName,
                          numericProjectId
                        )
                      }
                      disabled={selectedCount === 0}
                    >
                      Selected ({selectedCount})
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Types</DropdownMenuLabel>
                    {exportableTypes.map((type) => (
                      <DropdownMenuItem
                        key={`report-${type.id}`}
                        onClick={() =>
                          generatePunchListPdfReport(
                            exportSourceItems.filter((item) => item.item_type?.id === type.id),
                            type.name,
                            projectName,
                            numericProjectId
                          )
                        }
                        disabled={(visibleCountByType.get(type.id) ?? 0) === 0}
                      >
                        {type.name}
                      </DropdownMenuItem>
                    ))}
                    {hasUnitAreaReportOptions && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setUnitItemsAction('report')}>
                          By unit or area…
                        </DropdownMenuItem>
                        {unitReportOptions.length > 0 && (
                          <DropdownMenuItem onSelect={() => setUnitTourDialogOpen(true)}>
                            QR punch walks…
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    {(filterViews ?? []).length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Saved views</DropdownMenuLabel>
                        {(filterViews ?? []).map((view) => (
                          <DropdownMenuItem
                            key={`report-view-${view.id}`}
                            onClick={() => void generateFilterViewReport(view)}
                          >
                            {view.name}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    disabled={
                      (exportSourceItems.length === 0 && unitReportOptions.length === 0) ||
                      sendReminders.isPending ||
                      shareUnitReports.isPending
                    }
                  >
                    <Mail className="h-4 w-4" />
                    Share
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-[70vh] w-40 max-w-[calc(100vw-2rem)] overflow-y-auto">
                    <DropdownMenuItem
                      onClick={() => void sendReminderForItems(selectedItems)}
                      disabled={selectedCount === 0 || sendReminders.isPending}
                    >
                      Selected ({selectedCount})
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Types</DropdownMenuLabel>
                    {exportableTypes.map((type) => (
                      <DropdownMenuItem
                        key={`share-${type.id}`}
                        onClick={() =>
                          void sendReminderForItems(
                            exportSourceItems.filter((item) => item.item_type?.id === type.id)
                          )
                        }
                        disabled={
                          (visibleCountByType.get(type.id) ?? 0) === 0 || sendReminders.isPending
                        }
                      >
                        {type.name}
                      </DropdownMenuItem>
                    ))}
                    {unitReportOptions.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => setUnitReportShareDialogOpen(true)}
                          disabled={shareUnitReports.isPending}
                        >
                          By unit or area…
                        </DropdownMenuItem>
                      </>
                    )}
                    {(filterViews ?? []).length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Saved views</DropdownMenuLabel>
                        {(filterViews ?? []).map((view) => (
                          <DropdownMenuItem
                            key={`share-view-${view.id}`}
                            onClick={() => void sendFilterViewReminder(view)}
                            disabled={sendReminders.isPending}
                          >
                            {view.name}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => setCreateOpen(true)}
              disabled={!numericProjectId}
              data-tour-id="punch-lists-new-item"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Item
            </Button>
          </div>
        </div>

        <PunchListsNavigation projectId={numericProjectId} />

        {isSummaryVisible && (
          <div id="punch-lists-summary" className="min-w-0">
            <PunchListsDashboard projectId={numericProjectId} />
          </div>
        )}

        <PunchListsFilters
          projectId={numericProjectId}
          filters={filters}
          sort={sort}
          onFiltersChange={handleFiltersChange}
          onFilterViewApply={handleFilterViewApply}
          assignees={assigneeOptions}
          trades={tradeOptions}
          itemTypes={typeOptions}
          locations={locationOptions}
          areaTagSets={areaTagSetOptions}
          selectedCount={selectedCount}
          selectableItemCount={isComplete ? selectableItemIds.length : 0}
          allSelectableSelected={allSelectableSelected}
          partiallySelected={partiallySelected}
          onSelectAll={handleSelectAll}
          onSortChange={handleSortChange}
        />

        {!isLoading && !isComplete && !error && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading remaining items… {allItems.length} of {itemQuery.totalCount} loaded.
            {' '}Results and sorting will update as items arrive. Bulk actions are available when loading finishes.
          </p>
        )}
        {error && (
          <ErrorState
            error={error}
            title={items ? 'Unable to load remaining punch items' : 'Unable to load punch items'}
            onRetry={() => void (itemQuery.isFetchNextPageError
              ? itemQuery.fetchNextPage({ cancelRefetch: false })
              : itemQuery.refetch())}
          />
        )}
        {exportQuery.error && exportQuery.error !== error && (
          <ErrorState
            error={exportQuery.error}
            title="Unable to load all items for reports and bulk actions"
            onRetry={() => void (exportQuery.isFetchNextPageError
              ? exportQuery.fetchNextPage({ cancelRefetch: false })
              : exportQuery.refetch())}
          />
        )}

        <PunchListsTable
          items={pagedItems}
          isLoading={isLoading}
          error={items ? undefined : error}
          selectAllDisabled={!isComplete}
          onRowClick={handleRowClick}
          onEdit={(item) => setEditingItemId(item.id)}
          onDelete={(item) => void handleDelete(item)}
          sort={sort}
          onSortChange={handleColumnSortChange}
          selectedItemIds={selectedItemIds}
          allSelectableSelected={allSelectableSelected}
          partiallySelected={partiallySelected}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
        />

        {!isLoading && !error && filteredCount > 0 && (
          <PaginationControls
            state={paginationState}
            actions={paginationActions}
            info={paginationInfo}
          />
        )}
      </div>

      <PunchListCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        projectId={numericProjectId}
      />
      <PunchListCreateDialog
        open={!!editingItemId && !!editingItem}
        onClose={() => setEditingItemId(undefined)}
        projectId={numericProjectId}
        item={editingItem}
      />
      <UnitTourReportDialog
        open={unitTourDialogOpen}
        onOpenChange={setUnitTourDialogOpen}
        units={unitReportOptions}
        generating={generatingUnitTourReports}
        onGenerate={generateUnitReports}
      />
      <UnitReportShareDialog
        open={unitReportShareDialogOpen}
        onOpenChange={setUnitReportShareDialogOpen}
        units={unitReportOptions}
        users={assigneeOptions}
        sharing={shareUnitReports.isPending}
        onShare={shareUnitReportsByEmail}
      />
      <UnitItemsActionDialog
        action={unitItemsAction}
        layouts={layoutReportOptions}
        units={unitReportOptions}
        working={unitItemsActionPending}
        onOpenChange={(open) => {
          if (!open) setUnitItemsAction(null);
        }}
        onConfirm={handleUnitItemsAction}
      />
      {csvExportRequest && (
        <PunchListExportDialog
          columns={csvExportRequest.preferences.available_columns}
          initialSelectedColumns={csvExportRequest.preferences.selected_columns}
          exporting={updateExportPreferences.isPending}
          onOpenChange={(open) => {
            if (!open) setCsvExportRequest(null);
          }}
          onExport={handleCsvExport}
        />
      )}
    </BaseLayout>
  );
};

const getDateOnly = (iso: string | null | undefined) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const getServerFilters = (filters: PunchListItemFilters): PunchListItemFilters => ({
  status: filters.status,
  priority: filters.priority,
  assignee: filters.assignee,
  search: filters.search,
  hotspot_id: filters.hotspot_id,
  photo_id: filters.photo_id,
  area_tag_id: filters.area_tag_id,
  area_tag_set_id: filters.area_tag_set_id,
});

const getFilterDate = (value: string | undefined) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const isWithinDateRange = (
  iso: string | null | undefined,
  fromValue: string | undefined,
  toValue: string | undefined
) => {
  const from = getFilterDate(fromValue);
  const to = getFilterDate(toValue);
  if (!from && !to) return true;

  const date = getDateOnly(iso);
  if (!date) return false;
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
};

const matchesDueState = (
  iso: string | null | undefined,
  state: PunchListItemFilters['due_state']
) => {
  if (!state) return true;
  const date = getDateOnly(iso);
  if (state === 'no_due_date') return !date;
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sevenDaysFromToday = new Date(today);
  sevenDaysFromToday.setDate(today.getDate() + 7);

  if (state === 'overdue') return date < today;
  if (state === 'due_today') return date.getTime() === today.getTime();
  if (state === 'due_next_7') return date >= today && date <= sevenDaysFromToday;
  return true;
};

const filterPunchListItems = (items: PunchListItemSummary[], filters: PunchListItemFilters) =>
  items.filter((item) => {
    if (filters.assignee_unassigned && item.assignee) return false;
    if (filters.trade_id && item.trade?.id !== filters.trade_id) return false;
    if (filters.item_type_id && item.item_type?.id !== filters.item_type_id) return false;
    if (filters.location_id && item.location?.id !== filters.location_id) return false;
    if (!matchesDueState(item.due_date, filters.due_state)) return false;
    if (filters.visibility === 'private' && !item.is_private) return false;
    if (filters.visibility === 'shared' && item.is_private) return false;
    if (filters.hotspot_state === 'linked' && !item.hotspot_id) return false;
    if (filters.hotspot_state === 'unlinked' && item.hotspot_id) return false;
    if (!isWithinDateRange(item.due_date, filters.due_from, filters.due_to)) return false;
    if (!isWithinDateRange(item.created_at, filters.created_from, filters.created_to)) return false;
    return true;
  });

const STATUS_SORT_ORDER: Record<PunchListItemSummary['status'], number> = {
  OVERDUE: 0,
  OPEN: 1,
  PENDING_APPROVAL: 2,
  COMPLETED: 3,
  CLOSED: 4,
};

const PRIORITY_SORT_ORDER: Record<PunchListItemSummary['priority'], number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
};

const sortPunchListItems = (items: PunchListItemSummary[], sort: readonly PunchListsSortState[]) =>
  sortListItemsByRules(items, sort, (item, key) => {
    switch (key) {
      case 'status':
        return STATUS_SORT_ORDER[item.status];
      case 'priority':
        return PRIORITY_SORT_ORDER[item.priority];
      case 'item_type':
        return item.item_type?.name ?? null;
      case 'location':
        return item.location?.name ?? null;
      case 'due_date':
        return item.due_date ? Date.parse(item.due_date) : null;
      case 'created_at':
        return Date.parse(item.created_at);
    }
  });
